import { FormEvent, useState } from 'react'
import { formatDateTime, translate } from '../../configuration/localization'
import { getAllowedCampaignTransitions } from '../../domain/campaign/status'
import type { Campaign, CampaignStatus } from '../../types'
import { platformApi } from '../platform/public'

const statusLabelKeys: Record<CampaignStatus, 'campaign.status.draft' | 'campaign.status.configured' | 'campaign.status.active' | 'campaign.status.paused' | 'campaign.status.completed' | 'campaign.status.archived'> = {
  draft: 'campaign.status.draft',
  configured: 'campaign.status.configured',
  active: 'campaign.status.active',
  paused: 'campaign.status.paused',
  completed: 'campaign.status.completed',
  archived: 'campaign.status.archived',
}

export interface CampaignPanelProps {
  tenantId: string
  userId: string
  campaigns: Campaign[]
  selectedCampaignId: string
  canOperate: boolean
  onSelect: (campaignId: string) => void
  onReload: () => Promise<void>
}

export function CampaignPanel({ tenantId, userId, campaigns, selectedCampaignId, canOperate, onSelect, onReload }: CampaignPanelProps) {
  const [showCreate, setShowCreate] = useState(false)

  return <>
    <section id="campaigns" className="panel">
      <div className="panel-header"><div><p className="eyebrow">CAMPAIGN CORE</p><h2>Campaign</h2></div><button onClick={() => void onReload()}>{translate('common.refresh')}</button></div>
      {campaigns.length === 0
        ? <EmptyState onCreate={() => setShowCreate(true)} canCreate={canOperate} />
        : <div className="campaign-list">{campaigns.map((campaign) => <CampaignRow key={campaign.id} campaign={campaign} canOperate={canOperate} selected={campaign.id === selectedCampaignId} onOpen={() => onSelect(campaign.id)} onChanged={onReload} />)}</div>}
    </section>
    {showCreate && canOperate && <CreateCampaignDialog tenantId={tenantId} userId={userId} onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await onReload() }} />}
  </>
}

function CreateCampaignDialog({ tenantId, userId, onClose, onCreated }: { tenantId: string; userId: string; onClose: () => void; onCreated: () => Promise<void> }) {
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    const result = await platformApi.campaign.create({ tenantId, name, brandName: brand, description: description || null, createdBy: userId })
    setBusy(false)
    if (result.error) setError(result.error.message)
    else await onCreated()
  }

  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal" role="dialog" aria-modal="true" aria-labelledby="create-campaign-title" onMouseDown={(event) => event.stopPropagation()}>
    <div className="panel-header"><div><p className="eyebrow">NEW CAMPAIGN</p><h2 id="create-campaign-title">Tạo campaign</h2></div><button aria-label="Đóng" onClick={onClose}>×</button></div>
    <form className="stack" onSubmit={submit}>
      <label>Tên campaign<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>Brand<input required value={brand} onChange={(event) => setBrand(event.target.value)} /></label>
      <label>Mô tả<textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
      {error && <div className="notice error">{error}</div>}
      <div className="form-actions"><button type="button" onClick={onClose}>{translate('common.cancel')}</button><button className="primary" disabled={busy}>{busy ? 'Đang tạo…' : 'Tạo bản nháp'}</button></div>
    </form>
  </section></div>
}

function CampaignRow({ campaign, canOperate, selected, onOpen, onChanged }: { campaign: Campaign; canOperate: boolean; selected: boolean; onOpen: () => void; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pendingStatus, setPendingStatus] = useState<CampaignStatus | null>(null)
  const [reason, setReason] = useState('')

  async function transition(event: FormEvent) {
    event.preventDefault()
    if (!pendingStatus || !reason.trim()) return
    setBusy(true)
    setError('')
    const result = await platformApi.campaign.transition(campaign.id, pendingStatus, reason.trim())
    setBusy(false)
    if (result.error) {
      setError(result.error.message)
      return
    }
    setPendingStatus(null)
    setReason('')
    await onChanged()
  }

  return <article className={`campaign-row ${selected ? 'selected' : ''}`}>
    <div>
      <span className={`status status-${campaign.status}`}>{translate(statusLabelKeys[campaign.status])}</span>
      <h3>{campaign.name}</h3>
      <p>{campaign.brand_name} · Cập nhật {formatDateTime(campaign.updated_at)}</p>
      {campaign.description && <p className="muted">{campaign.description}</p>}
      {error && <p className="inline-error">{error}</p>}
      {pendingStatus && <form className="stack" onSubmit={transition} aria-label={`Xác nhận chuyển trạng thái sang ${translate(statusLabelKeys[pendingStatus])}`}>
        <label>Lý do chuyển sang “{translate(statusLabelKeys[pendingStatus])}”<textarea rows={2} required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <div className="form-actions"><button type="button" onClick={() => { setPendingStatus(null); setReason('') }}>{translate('common.cancel')}</button><button className="primary" disabled={busy || !reason.trim()}>{busy ? 'Đang cập nhật…' : 'Xác nhận chuyển trạng thái'}</button></div>
      </form>}
    </div>
    <div className="row-actions">
      <button className={selected ? 'primary' : ''} onClick={onOpen}>{selected ? (canOperate ? 'Đang mở operations' : 'Đang mở dashboard') : (canOperate ? 'Mở operations' : 'Mở dashboard')}</button>
      {canOperate && getAllowedCampaignTransitions(campaign.status).map((status) => <button disabled={busy} key={status} onClick={() => { setPendingStatus(status); setReason('') }}>{translate(statusLabelKeys[status])}</button>)}
    </div>
  </article>
}

function EmptyState({ onCreate, canCreate }: { onCreate: () => void; canCreate: boolean }) {
  return <div className="empty"><h3>Chưa có campaign</h3><p>{canCreate ? 'Tạo một campaign draft để kiểm tra tenant, role và state machine.' : 'Chưa có campaign nào được cấp quyền cho tài khoản này.'}</p>{canCreate && <button className="primary" onClick={onCreate}>Tạo campaign đầu tiên</button>}</div>
}
